import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useDownloadCSV, useGetEmployerIdParam } from '../../hooks';
import { ParticipantAccountActivityReportTableParams } from '../ParticipantActivityReport.types';

export const QUERY_KEY = 'EXPORT_PARTICIPANT_ACCOUNT_ACTIVITY_REPORT_CSV';
const useParticipantAccountActivityReportExportCSVQuery = (
  params: ParticipantAccountActivityReportTableParams
) => {
  const getEmployerIdParam = useGetEmployerIdParam();
  const handleSaveFile = useDownloadCSV();
  const [isLoading, setIsLoading] = useState(false);

  const formattedAsDate = useMemo(() => {
    if (!params.asDate) {
      return null;
    }
    const parsedDate = dayjs(params.asDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.asDate]);

  const { isSuccess, refetch } = useQuery(
    [QUERY_KEY],
    () =>
      api.get(API_URL.REPORTS_PARTICIPANT_ACCOUNT_ACTIVITY_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: _.pickBy({
          ...getEmployerIdParam(params.employerId, params.employerIds),
          show_ssn: params.isSSNShowed,
          as_of_date: formattedAsDate,
          plan_year_period:
            params?.planYearPeriod && !params?.planYearPeriod.includes(ALL_OPTION_KEY)
              ? params?.planYearPeriod.join(',')
              : undefined,
          enrollment_statuses:
            params.enrollmentStatuses && !params.enrollmentStatuses.includes(ALL_OPTION_KEY)
              ? params.enrollmentStatuses.join(',')
              : undefined,
          account_types:
            (params.isMultipleEmployer || params.accountTypes) &&
            !params.accountTypes.includes(ALL_OPTION_KEY)
              ? params.accountTypes?.join(',')
              : undefined,
          search_value: params.searchString,
          plan_ids:
            params?.reportPlanIds && !params?.reportPlanIds.includes(ALL_OPTION_KEY)
              ? params?.reportPlanIds.join(',')
              : undefined,
        }),
      }),
    {
      retry: 0,
      enabled: false,
    }
  );

  const handleExport = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await refetch();
      handleSaveFile(response.data?.data, 'csv', 'participant_account_activity');
    } finally {
      setIsLoading(false);
    }
  }, [handleSaveFile, refetch]);
  return {
    isLoading,
    isSuccess,
    handleExport,
  };
};

export default useParticipantAccountActivityReportExportCSVQuery;
